import React from "react";
import { QueryClient, QueryClientProvider } from "react-query";
import { Provider } from "react-redux";
import {
  createBrowserRouter,
  createRoutesFromElements,
  RouterProvider,
  Route,
  Navigate,
  redirect,
  type LoaderFunctionArgs,
} from "react-router-dom";
import { Store } from "redux";
import { LaunchDarklyProvider } from "shared-services";

import {
  AnalyticsProvider,
  Authenticator,
  AuthProvider,
  ErrorTrackingProvider,
} from "component-library";
import { IApplicationState } from "app/store";
import {
  AGENT_SEARCH_PARAM,
  AGENT_SEARCH_PATH,
  ALL_OPPORTUNITIES_PATH,
  ALL_PAID_PATH,
  BASE_PATH,
  BREAKDOWN_ID,
  BREAKDOWN_PATH,
  BREAKDOWN_PRINT_PATH,
  CLIENT_ALERTS_PATH,
  FOLLOW_UP_PATH,
  MY_FILTERS_FULL_PATH,
  MY_FILTERS_PATH,
  MY_SUGGESTIONS_PATH,
  ROLE_PATH,
  SELF_TAPE_PATH,
  SELF_TAPE_UPLOAD_PATH,
  SELF_TAPER_DETAIL_PATH,
} from "config/common";
import { checkIsYp } from "shared-auth";
import { getLastPathnameElement } from "pages/JobsPage/JobsPage.helpers";
import config from "config/global";
import ErrorBoundary from "app-shell/ErrorBoundary/ErrorBoundary";
import { GoogleTagManager } from "app-shell/GoogleTagManager/GoogleTagManager";
import JobsPage, {
  AllOpportunitiesPageWrapper,
  AllPaidPageWrapper,
  ClientAlertsPageWrapper,
  MatchingMyFilterPageWrapper,
  MySuggestionsWrapper,
  SelfTapeRequestsPageWrapper,
} from "pages/JobsPage/JobsPage";
import AgentSearchPageWrapper from "pages/AgentSearchPage/AgentSearchPageWrapper";
import SelfTapeRequestPage from "pages/SelfTapeRequestPage/SelfTapeRequestPage";
import SelfTapeUploadPage from "pages/SelfTapeUploadPage/SelfTapeUploadPage";
import BreakdownPage from "pages/JobPage/BreakdownPage/BreakdownPage";
import FollowUpPage from "pages/JobPage/FollowUpPage/FollowUpPage";
import RolePage from "pages/JobPage/RolePage/RolePage";
import Page404Container from "app-shell/Page404Container/Page404Container";
import BreakdownPrintPage from "pages/JobPage/BreakdownPrintPage/BreakdownPrintPage";
import App from "../../App";

interface IRootProps {
  store: Store<IApplicationState>;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

const VALID_TAB_PATHS = new Set([
  MY_FILTERS_PATH,
  ALL_PAID_PATH,
  ALL_OPPORTUNITIES_PATH,
  CLIENT_ALERTS_PATH,
  MY_SUGGESTIONS_PATH,
  SELF_TAPE_PATH,
]);

const jobsPageLoader = ({ request }: LoaderFunctionArgs) => {
  const url = new URL(request.url);
  const tab = getLastPathnameElement(url.pathname);

  if (tab && !VALID_TAB_PATHS.has(tab)) {
    // React Router v6 pattern: throw Response for 404 handling
    // eslint-disable-next-line
    throw new Response("Not Found", { status: 404 });
  }

  // Redirect YP users to self-tape-requests if they try to access other tabs
  const isYp = checkIsYp();

  if (isYp && tab && tab !== SELF_TAPE_PATH) {
    return redirect(`${BASE_PATH}/${SELF_TAPE_PATH}`);
  }

  return null;
};

const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route path={BASE_PATH} element={<App />}>
        <Route index element={<Navigate to={MY_FILTERS_FULL_PATH} replace />} />

        {/* Breakdown routes: /jobs/breakdowns/:breakdownId */}
        <Route path={BREAKDOWN_PATH}>
          <Route
            index
            element={<Navigate to={MY_FILTERS_FULL_PATH} replace />}
          />
          <Route path={BREAKDOWN_ID}>
            <Route index element={<BreakdownPage />} />
            <Route
              path={BREAKDOWN_PRINT_PATH}
              element={<BreakdownPrintPage />}
            />
            <Route path={ROLE_PATH} element={<RolePage />} />
            <Route path={FOLLOW_UP_PATH} element={<FollowUpPage />} />
          </Route>
        </Route>

        {/* Self-tape routes: /jobs/self-tape-requests/:performerRequestId */}
        <Route path={SELF_TAPER_DETAIL_PATH}>
          <Route index element={<SelfTapeRequestPage />} />
          <Route
            path={SELF_TAPE_UPLOAD_PATH}
            element={<SelfTapeUploadPage />}
          />
        </Route>

        {/* Agent search routes: /jobs/performers/search/:searchTerm? */}
        <Route
          path={`${AGENT_SEARCH_PATH}/${AGENT_SEARCH_PARAM}?`}
          element={<AgentSearchPageWrapper />}
        />

        {/* Jobs page layout with nested tab routes */}
        <Route element={<JobsPage />} errorElement={<Page404Container />}>
          <Route
            path={MY_FILTERS_PATH}
            element={<MatchingMyFilterPageWrapper />}
            loader={jobsPageLoader}
          />
          <Route
            path={ALL_OPPORTUNITIES_PATH}
            element={<AllOpportunitiesPageWrapper />}
            loader={jobsPageLoader}
          />
          <Route
            path={ALL_PAID_PATH}
            element={<AllPaidPageWrapper />}
            loader={jobsPageLoader}
          />
          <Route
            path={CLIENT_ALERTS_PATH}
            element={<ClientAlertsPageWrapper />}
            loader={jobsPageLoader}
          />
          <Route
            path={MY_SUGGESTIONS_PATH}
            element={<MySuggestionsWrapper />}
            loader={jobsPageLoader}
          />
          <Route
            path={SELF_TAPE_PATH}
            element={<SelfTapeRequestsPageWrapper />}
            loader={jobsPageLoader}
          />
        </Route>
      </Route>
      <Route path="*" element={<Page404Container />} />
    </>
  )
);

export class Root extends React.Component<IRootProps> {
  public render() {
    return (
      <AuthProvider>
        <Authenticator>
          <AnalyticsProvider>
            <ErrorTrackingProvider appName="nujobs-app">
              <Provider store={this.props.store}>
                <ErrorBoundary>
                  <GoogleTagManager {...config.gtm} />
                  <QueryClientProvider client={queryClient}>
                    <LaunchDarklyProvider>
                      <RouterProvider router={router} />
                    </LaunchDarklyProvider>
                  </QueryClientProvider>
                </ErrorBoundary>
              </Provider>
            </ErrorTrackingProvider>
          </AnalyticsProvider>
        </Authenticator>
      </AuthProvider>
    );
  }
}
